import { useCallback, useEffect, useRef } from "react";

/**
 * Resource-wide async single-flight with one coalesced rerun.
 *
 * Multiple poll/visibility/manual triggers for the same logical resource never
 * execute concurrently. If triggers arrive while one run is active, only the
 * latest argument set is executed once after the active run settles.
 */
export function useSingleFlightTask<Args extends unknown[], Result>(
  task: (...args: Args) => Promise<Result>,
): (...args: Args) => Promise<Result | undefined> {
  const taskRef = useRef(task);
  const activePromiseRef = useRef<Promise<Result | undefined> | null>(null);
  const latestArgsRef = useRef<Args | null>(null);
  const rerunPendingRef = useRef(false);
  const mountedRef = useRef(true);

  useEffect(() => {
    taskRef.current = task;
  }, [task]);

  useEffect(() => {
    // React StrictMode führt Effects in DEV absichtlich mount→cleanup→mount aus.
    // Der zweite Mount muss den Single-Flight-Runner wieder aktivieren.
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      latestArgsRef.current = null;
      rerunPendingRef.current = false;
    };
  }, []);

  return useCallback((...args: Args): Promise<Result | undefined> => {
    latestArgsRef.current = args;
    rerunPendingRef.current = true;
    if (activePromiseRef.current) return activePromiseRef.current;

    const promise: Promise<Result | undefined> = (async () => {
      let lastResult: Result | undefined;
      let firstError: unknown = null;
      while (mountedRef.current && rerunPendingRef.current) {
        rerunPendingRef.current = false;
        const runArgs = latestArgsRef.current;
        if (!runArgs) break;
        try {
          lastResult = await taskRef.current(...runArgs);
        } catch (error) {
          if (firstError === null) firstError = error;
        }
      }
      if (firstError !== null) throw firstError;
      return lastResult;
    })().finally(() => {
      if (activePromiseRef.current === promise) activePromiseRef.current = null;
    });

    activePromiseRef.current = promise;
    return promise;
  }, []);
}
